import { describeApiError, type Leaderboard } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Pill, Spinner } from '@brush/ui';
import { Award, Edit2, Plus, Target, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  useCreateLeaderboard,
  useCurrentUser,
  useDeleteLeaderboard,
  useFantasyContests,
  useLeaderboards,
  useRoomSummary,
  useUpdateLeaderboard,
} from '../../queries';
import { useRoom } from '../../room';
import { inWindow } from '../../summary';
import { LeaderboardBuilder } from './LeaderboardBuilder';
import { LeaderboardDetail } from './LeaderboardDetail';


/** §2.1's three states, in the room's own words. */
const STATUS_LABEL: Record<string, string> = {
  draft: 'Not started',
  running: 'Running',
  closed: 'Closed',
};

export function LeaderboardsView() {
  const { room, roomId } = useRoom();
  const me = useCurrentUser();
  const leaderboardsQ = useLeaderboards(roomId);
  const contestsQ = useFantasyContests(roomId);
  // Which boards are in window is the room summary's answer at the room's gaming day — the same one
  // Overview lists, so the two screens cannot disagree (known gap 22). Cached; the shell reads it too.
  const summaryQ = useRoomSummary(roomId);

  const createLeaderboard = useCreateLeaderboard(roomId);
  const updateLeaderboard = useUpdateLeaderboard(roomId);
  const deleteLeaderboard = useDeleteLeaderboard(roomId);

  const [viewingId, setViewingId] = useState<string | null>(null);

  /*
    What the dialog is aimed at, held as an **id** rather than as the board.

    The board itself goes stale the moment anything inside the dialog changes it, and Start is the
    case that hurt: it pins a points system and moves the board to Running, the list refetches, and
    the section went on offering the Draft picker because the object captured at open time still
    said `draft`. A successful mutation that does not visibly land invites the second click, which
    earns `leaderboard_already_started` — correct, and read as a fault in the product. An id is
    re-resolved against the list on every render, the way `viewingId` above already is.

    `mode` is what keeps **new** and **gone** apart. Held as `Leaderboard | null` those two spell
    themselves identically, so a board deleted from another tab would quietly retitle the dialog to
    "New leaderboard" and turn the operator's next Save into a second board. As a mode, absence in
    edit mode means exactly one thing, and the effect below is free to act on it.

    `created` is the response to a create the list has not caught up with — see `onCreate` below.
    It is the only such gap: every other way into edit mode opens from a card the list is already
    carrying.
  */
  const [builder, setBuilder] = useState<
    { mode: 'new' } | { mode: 'edit'; id: string; created?: Leaderboard } | null
  >(null);

  /*
    One question asked of the list — does it carry the board this dialog is aimed at? — with two
    answers worth acting on.

    **It does, and the dialog is still holding the create response**: retire the seed. Its whole
    life is the gap between a create returning and the invalidated list arriving, and keeping it
    past that would leave the dialog permanently unable to notice the board being deleted.

    **It does not, and there is no seed**: the board has gone, here or in another tab. Close the
    dialog — a form whose Save would 404, under a title naming a board that no longer exists, is
    worse than no dialog. `boards` being undefined is what separates "gone" from "not arrived yet",
    the distinction `ContestCard`'s `leaderboardsKnown` draws for the same reason.
  */
  const boards = leaderboardsQ.data;
  useEffect(() => {
    if (builder?.mode !== 'edit' || !boards) return;
    if (boards.some((l) => l.id === builder.id)) {
      if (builder.created) setBuilder({ mode: 'edit', id: builder.id });
    } else if (!builder.created) {
      setBuilder(null);
    }
  }, [builder, boards]);

  if (leaderboardsQ.isLoading) return <Spinner label="Loading leaderboards…" />;
  if (leaderboardsQ.isError || !room) {
    return (
      <div className="p-8">
        <ErrorRetry message="Couldn't load leaderboards." onRetry={() => void leaderboardsQ.refetch()} />
      </div>
    );
  }

  const leaderboards = leaderboardsQ.data ?? [];
  const contests = contestsQ.data ?? [];

  /*
    Resolved from the list on every render, which is what lets a change made inside the dialog reach
    it: `LeaderboardPointsSection` reads `status` off this prop to choose between the Draft picker
    and the pinned view. The create response answers until the list catches up, so the dialog does
    not blink back to "New leaderboard" in that gap and re-block the Start it had just unblocked.
  */
  const editing =
    builder?.mode === 'edit'
      ? (leaderboards.find((l) => l.id === builder.id) ?? builder.created ?? null)
      : null;

  const viewing = viewingId ? leaderboards.find((l) => l.id === viewingId) : null;
  if (viewing) return <LeaderboardDetail leaderboard={viewing} onBack={() => setViewingId(null)} />;

  return (
    <div className="p-8">
      {/*
       * The same header structure the tournaments list uses, minus the half it does not need.
       * `flex-wrap` with `gap-3` is the valve — the heading and this button come to about 328px
       * of the 366px `p-8` leaves at the 430px reference width, so they fit on one line there,
       * and a longer room name or a wider face drops the button to its own row instead of
       * squashing the heading. `min-w-0` lets the description wrap rather than force the row
       * wider than the viewport.
       *
       * No overflow menu here, deliberately: tournaments has one because it has a second action
       * to demote below `md`, and this section has a single action that stays at every size. An
       * empty menu beside it would be consistent in appearance and not in meaning.
       */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold text-ink-950">Leaderboards</h1>
          <p className="mt-1 text-sm text-ink-800/60">Build rankings from tournament results.</p>
        </div>
        <button
          type="button"
          onClick={() => setBuilder({ mode: 'new' })}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900"
        >
          <Plus className="size-4" /> New leaderboard
        </button>
      </div>

      <ErrorNotice
        message={
          describeApiError(createLeaderboard.error) ??
          describeApiError(updateLeaderboard.error) ??
          describeApiError(deleteLeaderboard.error)
        }
        className="mb-4"
      />

      {leaderboards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-800/20 bg-white p-12 text-center">
          <p className="text-sm text-ink-800/60">No leaderboards yet.</p>
          <p className="mt-1 text-xs text-ink-800/50">
            Create one to rank players across a set of tournaments over a date range.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {leaderboards.map((lb) => {
            const active = inWindow(lb, summaryQ.data);
            const attached = contests.filter((c) => c.leaderboardId === lb.id).length;
            return (
              <article key={lb.id} className="rounded-xl border border-ink-800/10 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-start justify-between">
                  <div className="min-w-0">
                    <h2 className="font-display font-semibold text-ink-950">{lb.name}</h2>
                    <p className="mt-0.5 text-xs text-ink-800/60">
                      {lb.dateStart} — {lb.dateEnd}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {/* The lifecycle status (§2.1), not "is today inside the date range" — a board
                        can sit inside its own window for weeks without anybody having started it,
                        and only a started one scores through a points system. */}
                    <Pill
                      tone={lb.status === 'running' ? 'good' : lb.status === 'closed' ? 'neutral' : 'warn'}
                    >
                      {STATUS_LABEL[lb.status] ?? lb.status}
                    </Pill>
                    {active && lb.status === 'running' ? (
                      <span className="rounded-md bg-felt-50 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-felt-900">
                        In window
                      </span>
                    ) : null}
                    <Award className="size-5 text-gold-500" />
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-ink-800/70">
                  {/* The pinned points system version, which is what this board scores with. It
                      replaced a formula name that lived on the leaderboard; a Draft has none,
                      because pinning is what starting does.

                      Deliberately no tournament count beside it: `tournamentIds` is the fantasy
                      layer's list and says nothing about what this leaderboard scores — which is
                      its scope. Showing it here is what made the picker look like the way to
                      choose scored events. */}
                  <span className="flex items-center gap-1">
                    <Target className="size-3" />
                    {lb.pointsSystemVersionNumber
                      ? `Points system v${lb.pointsSystemVersionNumber}`
                      : 'No points system yet'}
                  </span>
                  {attached > 0 ? (
                    <span className="flex items-center gap-1 text-ink-800/50">
                      <Trophy className="size-3" /> {attached} fantasy contest
                      {attached === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setViewingId(lb.id)}
                    className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900"
                  >
                    View standings
                  </button>
                  <button
                    type="button"
                    onClick={() => setBuilder({ mode: 'edit', id: lb.id })}
                    aria-label={`Edit ${lb.name}`}
                    className="rounded-lg border border-ink-800/15 px-3 py-2 hover:bg-felt-50"
                  >
                    <Edit2 className="size-4 text-ink-800/70" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {builder ? (
        <LeaderboardBuilder
          initial={editing}
          roomId={room.id}
          contestCount={editing ? contests.filter((c) => c.leaderboardId === editing.id).length : 0}
          isAdmin={me.data?.isAdmin ?? false}
          saving={createLeaderboard.isPending || updateLeaderboard.isPending}
          deleting={deleteLeaderboard.isPending}
          /*
            Creating **keeps the dialog open**, re-aimed at the board that now exists, rather than
            closing it. Attaching a points system is `POST /{id}/start` and nothing else — neither
            the create nor the PUT carries one, because the pin is immutable once made (§2.3) — so
            the picker in this dialog cannot be applied by a save and can only be *kept* for the
            Start that follows. Closing here unmounted it and discarded the operator's choice with
            nothing said, which left the dialog's own instruction ("Save the leaderboard first")
            impossible to follow without picking the system a second time.

            The response is carried as the seed above rather than only its id, because the id alone
            resolves to nothing until the invalidated list comes back — and the effect above would
            read that as a board that had stopped existing.
          */
          onCreate={(body) =>
            createLeaderboard.mutate(
              { ...body, roomId: room.id },
              { onSuccess: (created) => setBuilder({ mode: 'edit', id: created.id, created }) },
            )
          }
          onUpdate={(body) => {
            if (!editing) return;
            updateLeaderboard.mutate({ id: editing.id, body }, { onSuccess: () => setBuilder(null) });
          }}
          onDelete={() => {
            if (!editing) return;
            deleteLeaderboard.mutate(editing.id, {
              onSuccess: () => {
                setBuilder(null);
                setViewingId(null);
              },
            });
          }}
          onClose={() => setBuilder(null)}
        />
      ) : null}
    </div>
  );
}
